import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import SocialLoginButtons from "@/components/SocialLoginButtons";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { GraduationCap, User } from "lucide-react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { toast } from "@/hooks/use-toast";
import { useAuth } from "@/lib/auth";
import { useHoneypot } from "@/lib/honeypot";
import { captureReferralFromUrl, getStoredReferralCode, clearStoredReferralCode } from "@/lib/referral";
import { rememberPostSignupReturn } from "@/lib/postSignupReturn";
import AccountReopenForm from "@/components/AccountReopenForm";
import AuthShell from "@/components/AuthShell";
import { Trans, useTranslation } from "react-i18next";
import { oauthErrorKey, readOAuthRefusal } from "@/lib/oauthErrors";

const SignUp = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { signUp } = useAuth();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  // A link can preselect the tutor role (?role=tutor) so a would-be tutor
  // doesn't have to spot and flip the toggle; anything else stays a student.
  const [role, setRole] = useState(() => (searchParams.get("role") === "tutor" ? "tutor" : "student"));
  const [loading, setLoading] = useState(false);
  const [previouslyUsed, setPreviouslyUsed] = useState(false);
  const [acceptedLegal, setAcceptedLegal] = useState(false);
  // #722: the yes to news and offers by email. Its own box, unticked until the
  // person ticks it: that yes cannot be folded into the Terms box above it,
  // and nobody says it by not answering.
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  // #993: a click on any signup button without the tick answers by pointing at
  // the consent box (it sits below the whole email form, so a greyed button
  // gave no clue). True until the box is ticked.
  const [consentNudge, setConsentNudge] = useState(false);
  const consentBoxRef = useRef<HTMLDivElement>(null);
  const honeypot = useHoneypot();
  // Capture `?ref=CODE` synchronously on first render (before the social
  // buttons render) and stash it so it survives the OAuth round-trip.
  const [referralCode] = useState<string | null>(() => {
    captureReferralFromUrl();
    // A tutor's public page sends the visitor here with ?returnTo=/student/tutor/<id>
    // (#669); remembered now because the query string does not survive the form.
    rememberPostSignupReturn(searchParams.get("returnTo"));
    return getStoredReferralCode();
  });

  const nudgeConsent = () => {
    setConsentNudge(true);
    // scrollIntoView is missing under jsdom, hence the optional call.
    consentBoxRef.current?.scrollIntoView?.({ behavior: "smooth", block: "center" });
    // preventScroll: focusing the still-off-screen checkbox would otherwise
    // cancel the smooth scroll with an instant nearest-edge jump.
    document.getElementById("accept-legal")?.focus({ preventScroll: true });
  };

  // A refused Google/Apple sign-up lands here as ?error_code= (#865). The usual
  // one is consent_required: the click was one tick from succeeding, so say so
  // and point at the box, instead of leaving the visitor on a page that can't
  // finish it. The chosen role (?role=) is read above and survives.
  useEffect(() => {
    const refusal = readOAuthRefusal(window.location.search);
    if (!refusal) return;
    toast({
      title: t("auth.oauthErrors.title"),
      description: refusal.legacyMessage ?? t(oauthErrorKey(refusal.code), { provider: refusal.provider }),
      variant: "destructive",
    });
    if (refusal.code === "consent_required") nudgeConsent();
    const role = new URLSearchParams(window.location.search).get("role");
    window.history.replaceState({}, "", role ? `${window.location.pathname}?role=${encodeURIComponent(role)}` : window.location.pathname);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!acceptedLegal) {
      nudgeConsent();
      return;
    }
    setLoading(true);
    try {
      const { user, errors, emailPreviouslyUsed } = await signUp({
        emailAddress: email,
        password,
        firstName,
        lastName,
        role,
        acceptedLegal,
        marketingOptIn,
        website: honeypot.website,
        formElapsedMs: honeypot.elapsedMs(),
        referralCode: referralCode ?? undefined,
        // Base language code ("pt-BR" → "pt"): sets ui_locale from birth so the
        // welcome email + founder-thread message arrive in this language.
        uiLocale: i18n.language?.split("-")[0],
      });
      if (user) {
        clearStoredReferralCode();
        if (user.role === "tutor") {
          navigate("/onboarding/tutor");
        } else {
          navigate("/onboarding/student");
        }
      } else if (emailPreviouslyUsed) {
        // Swap the signup form for the reopen-request flow (notifies support).
        setPreviouslyUsed(true);
      } else {
        toast({ title: t("auth.signUp.toastFailedTitle"), description: errors[0], variant: "destructive" });
      }
    } catch (err) {
      console.error("SignUp error:", err);
      toast({ title: t("auth.signUp.toastErrorTitle"), description: err instanceof Error ? err.message : t("auth.signUp.toastGenericError"), variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell>
        <Card className="border-border/50">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl">{t("auth.signUp.title")}</CardTitle>
            <CardDescription>{t("auth.signUp.subtitle")}</CardDescription>
          </CardHeader>
          <CardContent>
            {previouslyUsed ? (
              <div className="space-y-4">
                <AccountReopenForm email={email} />
                {/* The reopen form replaces the email field, so without this the
                    user is stuck if they mistyped — let them go back and edit it. */}
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full"
                  onClick={() => setPreviouslyUsed(false)}
                >
                  {t("nav.back")}
                </Button>
              </div>
            ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <input {...honeypot.inputProps} />

              <div className="space-y-3">
                <Label>{t("auth.signUp.roleQuestion")}</Label>
                <RadioGroup value={role} onValueChange={setRole} className="grid grid-cols-2 gap-3">
                  <Label htmlFor="student" className={`flex items-center gap-2 p-4 rounded-lg border cursor-pointer transition-colors ${role === "student" ? "border-primary bg-primary/10" : "border-border hover:border-foreground/50"}`}>
                    <RadioGroupItem value="student" id="student" />
                    <GraduationCap className="w-4 h-4" /><span className="text-sm font-medium">{t("auth.signUp.roleLearn")}</span>
                  </Label>
                  <Label htmlFor="tutor" className={`flex items-center gap-2 p-4 rounded-lg border cursor-pointer transition-colors ${role === "tutor" ? "border-primary bg-primary/10" : "border-border hover:border-foreground/50"}`}>
                    <RadioGroupItem value="tutor" id="tutor" />
                    <User className="w-4 h-4" /><span className="text-sm font-medium">{t("auth.signUp.roleTeach")}</span>
                  </Label>
                </RadioGroup>
              </div>

              <SocialLoginButtons role={role} requireConsent consentGiven={acceptedLegal} marketingOptIn={marketingOptIn} referralCode={referralCode ?? undefined} onConsentMissing={nudgeConsent} />

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="firstName">{t("auth.signUp.firstNameLabel")}</Label>
                  <Input
                    id="firstName"
                    placeholder={t("auth.signUp.firstNamePlaceholder")}
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName">{t("auth.signUp.lastNameLabel")}</Label>
                  <Input
                    id="lastName"
                    placeholder={t("auth.signUp.lastNamePlaceholder")}
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">{t("auth.signUp.emailLabel")}</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder={t("auth.signUp.emailPlaceholder")}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">{t("auth.signUp.passwordLabel")}</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                />
              </div>

              {/* -mx-3 px-3 keeps the box's footprint constant sideways, so the
                  nudge ring/background toggle is paint-only — no checkbox
                  jumping under the pointer at the moment it's ticked. No
                  vertical negative margin: Tailwind 4's space-y-4 puts the gap
                  on each child's margin-bottom, so a -my-* here replaced the
                  box's own 16px with a negative one and slid the nudge ring
                  under the submit button. */}
              {/* One group for the two boxes, so they read as the two questions
                  of the form and not as one question and a stray line. */}
              <div className="space-y-1">
              <div ref={consentBoxRef} className={`-mx-3 rounded-lg px-3 py-2 ${consentNudge ? "ring-2 ring-destructive/70 bg-destructive/5" : ""}`}>
                <div className="flex items-start gap-2">
                  <Checkbox
                    id="accept-legal"
                    checked={acceptedLegal}
                    aria-required="true"
                    aria-describedby={consentNudge ? "consent-nudge-message" : undefined}
                    onCheckedChange={(v) => {
                      setAcceptedLegal(v === true);
                      if (v === true) setConsentNudge(false);
                    }}
                    className="mt-0.5"
                  />
                  <Label htmlFor="accept-legal" className="text-xs font-normal leading-snug text-muted-foreground">
                    {/* The links carry their English text as static children so
                        jsx-a11y sees accessible content; Trans swaps in each
                        locale's <terms>/<privacy> tag content at render time. */}
                    <Trans
                      i18nKey="auth.signUp.acceptLegal"
                      components={{
                        terms: <Link to="/terms" target="_blank" className="underline">Terms of Service</Link>,
                        privacy: <Link to="/privacy" target="_blank" className="underline">Privacy Policy</Link>,
                      }}
                    />
                  </Label>
                </div>
                {/* Permanently mounted: a live region injected together with
                    its text is unreliably announced — screen readers need the
                    region to exist before the content lands. Empty <p> has no
                    line box, so it costs no space while quiet. */}
                <p id="consent-nudge-message" role="status" className={`text-xs font-medium text-destructive ${consentNudge ? "mt-2" : ""}`}>
                  {consentNudge ? t("auth.signUp.consentNudge") : ""}
                </p>
              </div>

              {/* Outside the box above on purpose: the red nudge ring and its
                  message are about the required tick, and must never read as
                  "tick this one too". Same -mx-3 px-3 py-2 so both line up. */}
              <div className="-mx-3 flex items-start gap-2 px-3 py-2">
                <Checkbox
                  id="marketing-opt-in"
                  checked={marketingOptIn}
                  onCheckedChange={(v) => setMarketingOptIn(v === true)}
                  className="mt-0.5"
                />
                <Label htmlFor="marketing-opt-in" className="text-xs font-normal leading-snug text-muted-foreground">
                  {t("auth.signUp.marketingOptIn")}
                </Label>
              </div>
              </div>

              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? t("auth.signUp.submitLoading") : t("auth.signUp.submit")}
              </Button>
            </form>
            )}

            <p className="text-center text-sm text-muted-foreground mt-6">
              {t("auth.signUp.haveAccount")}{" "}
              <Link to="/signin" className="text-foreground hover:underline font-medium">{t("auth.signUp.signInLink")}</Link>
            </p>
          </CardContent>
        </Card>
    </AuthShell>
  );
};

export default SignUp;
